import * as Clipboard from 'expo-clipboard'
import { Control, FormState } from 'react-hook-form'
import { Platform, View } from 'react-native'
import { Tooltip } from 'react-native-paper'
import { StyleSheet } from 'react-native-unistyles'

import FormFieldCheckbox from '@/components/form/FormFieldCheckbox'
import FormFieldDate from '@/components/form/FormFieldDate'
import FormFieldText from '@/components/form/FormFieldText'
import FormFieldGender from '@/components/fosters/cards/formFields/FormFieldGender'
import FormFieldSpecies from '@/components/fosters/cards/formFields/FormFieldSpecies'
import FormFieldVolunteer from '@/components/fosters/cards/formFields/FormFieldVolunteer'
import FormFieldWeight from '@/components/fosters/cards/formFields/FormFieldWeight'
import fosterSharedStyles from '@/components/fosters/cards/fosterSharedStyles'
import { FosterForm } from '@/components/fosters/fosterFormSchema'
import FosterThumbnailEdit from '@/components/fosters/FosterThumbnailEdit'
import Card from '@/components/shared/Card'
import Skeleton from '@/components/shared/Skeleton'
import Snackbar from '@/components/snackbar/Snackbar'
import TourSpot from '@/components/tour/TourSpot'
import UniIconButton from '@/components/unistyles/UniIconButton'
import useAppTour from '@/hooks/useAppTour'
import useUserRoles from '@/hooks/useUserRoles'
import FosterService from '@/services/fosterService'
import { AppTourKeys } from '../../../../constants'

interface Props {
  control: Control<FosterForm, any>
  disabled: boolean
  formState: FormState<FosterForm>
  fosterId: string
}

const FosterProfileCard = ({
  control,
  disabled,
  formState,
  fosterId,
}: Props) => {
  const { foster, isLoading } = FosterService.useFoster(fosterId)

  const { isUserAdmin } = useUserRoles()

  useAppTour({ enabled: true, tourKey: AppTourKeys.FosterProfileTab })

  return (
    <TourSpot id="foster-profile-tab">
      <View style={styles.detailContainer}>
        <FosterThumbnailEdit disabled={disabled} fosterId={fosterId} />
        <Card title="Foster Details">
          <View style={fosterSharedStyles.textInputContainer}>
            <FormFieldText
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName="name"
              isLoading={isLoading}
              label="Name"
              required={true}
              style={[styles.fosterDetailInput, styles.nameFlexBasis]}
            />
            <FormFieldSpecies
              control={control}
              disabled={disabled}
              errors={formState.errors}
              isLoading={isLoading}
              style={[styles.fosterDetailInput, styles.speciesFlexBasis]}
            />
            <View
              style={[styles.volunteerContainer, styles.volunteerFlexBasis]}
            >
              <FormFieldVolunteer
                control={control}
                disabled={disabled}
                formState={formState}
                isLoading={isLoading}
                style={styles.fosterDetailInput}
              />
              {isUserAdmin && (
                <Tooltip title="Copy volunteer email">
                  <UniIconButton
                    icon="email-multiple-outline"
                    onPress={async () => {
                      if (foster?.user.email) {
                        await Clipboard.setStringAsync(foster?.user.email)

                        // Android has a system prompt that appears when something is copied, so we don't need to show something ourselves
                        if (Platform.OS !== 'android') {
                          Snackbar.info('Volunteer email copied to clipboard')
                        }
                      }
                    }}
                  />
                </Tooltip>
              )}
            </View>
            <FormFieldGender
              control={control}
              disabled={disabled}
              errors={formState.errors}
              isLoading={isLoading}
              style={[styles.fosterDetailInput, styles.speciesFlexBasis]}
            />
            <FormFieldText
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName="breed"
              isLoading={isLoading}
              label="Breed"
              required={false}
              style={[styles.fosterDetailInput, styles.fullWidthFlexBasis]}
            />
            <FormFieldText
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName="currentFood"
              isLoading={isLoading}
              label="Current Food"
              required={false}
              style={[styles.fosterDetailInput, styles.fullWidthFlexBasis]}
            />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName="birthDate"
              isLoading={isLoading}
              label="Birth Date"
              required={false}
              style={styles.fosterDetailInput}
            />
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName="intakeDate"
              isLoading={isLoading}
              label="Intake Date"
              required={false}
              style={styles.fosterDetailInput}
            />
            <View style={[styles.fosterDetailInput, { maxWidth: 140 }]}>
              <Skeleton isLoading={isLoading}>
                <FormFieldWeight
                  control={control}
                  disabled={disabled}
                  errors={formState.errors}
                  isLoading={isLoading}
                  weightDate={foster?.weightHistory?.[0]?.createdAt}
                />
              </Skeleton>
            </View>
            <FormFieldCheckbox
              control={control}
              disabled={disabled}
              fieldName="isHospice"
              isLoading={isLoading}
              label="Hospice"
              required={false}
              status="checked"
            />
          </View>
        </Card>
      </View>
    </TourSpot>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    detailContainer: {
      columnGap: theme.tokens.spacing[4],
      flexDirection: {
        xs: 'column',
        sm: 'column',
        md: 'row',
      },
      rowGap: theme.tokens.spacing[4],
    },
    fosterDetailInput: {
      flexBasis: {
        xs: '40%',
        md: '30%',
      },
      flexGrow: 1,
      minWidth: 125,
    },
    fullWidthFlexBasis: {
      flexBasis: {
        xs: '100%',
        md: '30%',
      },
    },
    nameFlexBasis: {
      flexBasis: {
        xs: '60%',
        md: '35%',
      },
    },
    speciesFlexBasis: {
      flexBasis: {
        xs: '20%',
        md: '10%',
      },
    },
    volunteerContainer: {
      alignItems: 'center',
      flexDirection: 'row',
    },
    volunteerFlexBasis: {
      flexBasis: {
        xs: '100%',
        md: '40%',
      },
    },
  }
})

export default FosterProfileCard
